import React from "react";
import {
  Row,
  Col,
  Form,
  FloatingLabel,
  Button,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-multi-date-picker";
import useAdjustTimeAndText from "./useAdjustTimeAndText";
import {
  IAdjustTimeAndTextState,
  IAdjustTimeAndTextStateRef,
  IAlertTypes,
} from "../../../common/types/alertsTypes";
import { ITemplate } from "../../../common/types/templateTypes";

interface IAdjustTimeAndTextProps {
  onNextTab: () => void;
  onClose: () => void;
  selectedTemplate: IAlertTypes;
  handleTemplateClick: (templateData: IAlertTypes) => void;
  isDateTimeFieldEmpty: (isAnyFieldEmpty: boolean) => void;
  adjustTimeAndTextState: IAdjustTimeAndTextState;
  setAdjustTimeAndTextState: (state: IAdjustTimeAndTextState) => void;
  adjustTimeAndTextStateRef: IAdjustTimeAndTextStateRef;
  isEdit: boolean;
  isQuickAlert: boolean;
  isEditQuickAlert: boolean;
  allClearChecked: boolean;
  setAllClearCheckbox: (allClearChecked: boolean) => void;
  allClearTime: string;
  setAllClearTime: (time: string) => void;
  templateLookup: Map<number, ITemplate>;
}

const AdjustTimeAndText: React.FC<IAdjustTimeAndTextProps> = ({
  onNextTab,
  onClose,
  selectedTemplate,
  handleTemplateClick,
  isDateTimeFieldEmpty,
  adjustTimeAndTextState,
  setAdjustTimeAndTextState,
  adjustTimeAndTextStateRef,
  isEdit,
  isQuickAlert,
  isEditQuickAlert,
  allClearChecked,
  setAllClearCheckbox,
  allClearTime,
  setAllClearTime,
  templateLookup,
}) => {
  const {
    isSendNowChecked,
    handleCheckboxChange,
    handleStartDateChange,
    handleEndDateChange,
    startTime,
    handleStartTimeChange,
    endTime,
    handleEndTimeChange,
    timeZone,
    handleTimeZoneChange,
    severityLevel,
    handleRadioChange,
    alertMessage,
    handleAlertMessageChange,
    alertHeading,
    handleAlertHeadingChange,
    handleNextButtonClick,
    isAnyFieldEmpty,
    startDate,
    endDate,
    timeZones,
    alertIcon,
    alertIcons,
    allTextColors,
    allTextColor,
    setAllTextColor,
    handleIconChange,
    handleAllTextColorChange,
    hideIcon,
    hideAllTextColor,
    showLogo,
    hideHeading,
    hideAlertMessage,
    mimoLogos,
    mimoLogo,
    handleLogoChange,
    showZipCode,
    zipCode,
    handleZipCodeChange,
    alertMessageNewLine,
    handleAllClearCheckboxChange,
    minimumSelectableDate,
  } = useAdjustTimeAndText(
    selectedTemplate,
    onNextTab,
    isQuickAlert,
    isEditQuickAlert,
    handleTemplateClick,
    isDateTimeFieldEmpty,
    adjustTimeAndTextState,
    setAdjustTimeAndTextState,
    adjustTimeAndTextStateRef,
    allClearChecked,
    setAllClearCheckbox,
    allClearTime,
    templateLookup,
  );

  const hasValue = (value: any) =>
  Array.isArray(value) ? value.length > 0 : Boolean(value);

  return (
    <div>
      <div className="adjust-time-and-text-container">
        <Row>
          <Col sm={5}>
            <div className="time-and-text-container">
              <h2 className="heading">Schedule Time to Send</h2>
              <div className="time-container">
                <div className="time-checkbox">
                  <OverlayTrigger
                    key={"auto-clear"}
                    placement="top"
                    overlay={
                      isEdit ? (
                        <Tooltip id={`tooltip-auto-clear`}>
                          Alerts can not be sent immediately while editing.
                        </Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <div
                      className="integration-input-content mb-3"
                      id="auto-clear"
                    >
                      <div className="integration-input">
                        <Form.Check
                          label={
                            !isQuickAlert && !isEditQuickAlert
                              ? "Send now and set this alert to be auto cleared after"
                              : "Set this alert to be auto cleared after"
                          }
                          checked={allClearChecked}
                          style={{ fontSize: "16px", fontFamily: "Roboto" }}
                          onChange={(e) =>
                            handleAllClearCheckboxChange(e.target.checked)
                          }
                          disabled={isEdit}
                        />

                        <span>
                          <Form.Select
                            disabled={!allClearChecked}
                            aria-label="select all clear time"
                            onChange={(e) => setAllClearTime(e.target.value)}
                            value={allClearTime}
                          >
                            <option value="">Select Time</option>
                            <option value="30">30s</option>
                            <option value="60">1m</option>
                            <option value="120">2m</option>
                          </Form.Select>
                        </span>
                      </div>
                    </div>
                  </OverlayTrigger>
                </div>
                {!isQuickAlert && !isEditQuickAlert && (
                  <>
                    <Form>
                      {["checkbox"].map((type, index) => (
                        <OverlayTrigger
                          key={index}
                          placement="top"
                          overlay={
                            isEdit ? (
                              <Tooltip id={`tooltip-${type}`}>
                                Alerts can not be sent immediately while
                                editing.
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <div key={type} className="mb-3">
                            <Form.Check
                              disabled={isEdit}
                              id={type}
                              label="Send now until the alert is ended manually"
                              checked={isSendNowChecked}
                              onChange={handleCheckboxChange}
                            />
                          </div>
                        </OverlayTrigger>
                      ))}
                    </Form>

                    {!allClearChecked && !isSendNowChecked ? (
                      <>
                        <div className="adjust-time-date-picker">
                          <label>Start Date</label>
                          <DatePicker
                            placeholder="Select date or date range..."
                            inputClass={hasValue(startDate) ? "date-time-value" : "date-time-placeholder"}
                            // render={<InputIcon />}
                            value={startDate}
                            onChange={handleStartDateChange}
                            disabled={isSendNowChecked || allClearChecked}
                            minDate={minimumSelectableDate}
                            maxDate={endDate || undefined}
                          />
                        </div>
                        <div className="adjust-time-date-picker">
                          <label>End Date</label>
                          <DatePicker
                            placeholder="Select date or date range..."
                            inputClass={hasValue(endDate) ? "date-time-value" : "date-time-placeholder"}
                            // render={<InputIcon />}
                            value={endDate}
                            onChange={handleEndDateChange}
                            disabled={isSendNowChecked || allClearChecked}
                            minDate={startDate || minimumSelectableDate}
                            
                          />
                        </div>
                        <div className="time-content">
                          <div className="time">
                            <label>Start Time:</label>
                            <input
                              type="time"
                               className={startTime ? "date-time-value" : "date-time-placeholder"}
                              value={startTime}
                              onChange={handleStartTimeChange}
                              disabled={isSendNowChecked || allClearChecked}
                            />
                          </div>
                          <div className="time">
                            <label>End Time:</label>
                            <input
                              type="time"
                              className={endTime ? "date-time-value" : "date-time-placeholder"}
                              value={endTime}
                              onChange={handleEndTimeChange}
                              disabled={isSendNowChecked || allClearChecked}
                            />
                          </div>
                          <div className="time">
                            <label>Time Zone:</label>
                            <Form.Select
                              className={timeZone ? "date-time-value" : "date-time-placeholder"}
                              aria-label="Default select example"
                              value={timeZone}
                              onChange={handleTimeZoneChange}
                              disabled={isSendNowChecked}
                            >
                              {timeZones.map((tz) => (
                                <option key={tz.value} value={tz.value}>
                                  {tz.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </div>

              <div className="severity-level">
                <h2>Choose the severity Level:</h2>
                <Form>
                  {["low", "medium", "high"].map((value) => (
                    <div key={`inline-${value}`} className="mb-3">
                      <Form.Check
                        type="radio"
                        inline
                        label={value.charAt(0).toUpperCase() + value.slice(1)}
                        name="group1"
                        id={`inline-${value}`}
                        value={value}
                        checked={severityLevel === value}
                        onChange={handleRadioChange}
                      />
                    </div>
                  ))}
                </Form>
              </div>
              {hideIcon ? (
                <div className="icon">
                  <label>Icons:</label>
                  <Form.Select
                    aria-label="Default Alert Icon"
                    value={alertIcon}
                    onChange={handleIconChange}
                  >
                    {alertIcons.map((icon, index) => (
                      <option key={index} value={icon.value}>
                        {icon.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ) : null}

              {hideAllTextColor ? (
                <div className="text-container">
                  <label>Background Color:</label>
                  <Form.Select
                    aria-label="Default Alert Template Color"
                    value={allTextColor}
                    onChange={handleAllTextColorChange}
                  >
                    {allTextColors.map((color, index) => (
                      <option key={index} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ) : null}

              {showLogo ? (
                <div className="icon">
                  <label>Logo:</label>
                  <Form.Select
                    aria-label="Default Alert Icon"
                    value={mimoLogo}
                    onChange={handleLogoChange}
                  >
                    {mimoLogos?.map((icon, index) => (
                      <option key={index} value={icon.value}>
                        {icon.label}
                      </option>
                    ))}
                    \{" "}
                  </Form.Select>
                </div>
              ) : null}
              {hideHeading ? (
                <div className="text-container" style={{ margin: "10px 0px" }}>
                  <label>Alert Title:</label>
                  <FloatingLabel
                    controlId="floatingTextarea2"
                    label="Alert Title"
                  >
                    <Form.Control
                      as="textarea"
                      placeholder="East Wing closed from 12:00PM November 1st to 5PM November 4th."
                      style={{ height: "100px" }}
                      value={alertHeading}
                      onChange={handleAlertHeadingChange}
                    />
                  </FloatingLabel>
                  <small
                    style={{
                      color:
                        alertHeading?.length ===
                        (selectedTemplate?.maxAlertTitleCharacter ?? 0)
                          ? "red"
                          : "#666",
                    }}
                  >
                    Character Limit: {alertHeading?.length}/
                    {selectedTemplate?.maxAlertTitleCharacter ?? 0}
                  </small>
                  {/* {isReachHeadingCharAndLineLimit ? <span className='alertErrorMessage'>{alertHeadingError}</span> : null} */}
                </div>
              ) : null}

              {hideAlertMessage ? (
                <div className="text-container">
                  <label>Write Alert Message:</label>
                  <FloatingLabel controlId="floatingTextarea2" label="Comments">
                    <Form.Control
                      as="textarea"
                      placeholder="East Wing closed from 12:00PM November 1st to 5PM November 4th."
                      style={{ height: "100px" }}
                      value={alertMessage}
                      onChange={handleAlertMessageChange}
                    />
                  </FloatingLabel>
                  <small
                    style={{
                      color:
                        alertMessage?.length ===
                        (selectedTemplate?.maxAlertMessageCharacter ?? 0)
                          ? "red"
                          : "#666",
                    }}
                  >
                    Character Limit: {alertMessage?.length}/
                    {selectedTemplate?.maxAlertMessageCharacter ?? 0}
                  </small>
                  {"  "}
                  <small
                    style={{
                      color:
                        alertMessageNewLine ===
                        (selectedTemplate?.maxAlertMessageLine ?? 0)
                          ? "red"
                          : "#666",
                    }}
                  >
                    Line Limit: {alertMessageNewLine}/
                    {selectedTemplate?.maxAlertMessageLine ?? 0}
                  </small>
                  {/* {isReachMessageCharAndLineLimit ? <span className='alertErrorMessage'>{alertMessageError}</span> : null} */}
                </div>
              ) : null}
              {showZipCode ? (
                <div className="text-container">
                  <label>Zip Code:</label>
                  <FloatingLabel controlId="floatingTextarea2" label="Zip Code">
                    <Form.Control
                      type="text"
                      pattern="^\d{5}(-\d{4})?$"
                      maxLength={10}
                      placeholder="East Wing closed from 12:00PM November 1st to 5PM November 4th."
                      style={{ height: "100px" }}
                      value={zipCode}
                      onChange={handleZipCodeChange}
                    />
                  </FloatingLabel>
                </div>
              ) : null}
            </div>
          </Col>
          <Col sm={1}>
            <div className="line"></div>
          </Col>
          <Col sm={6}>
            <div className="alert-view-container">
              <div className="alert-view-bg">
                <div className="alert-top-view-text">{alertHeading}</div>
                <div className="alert-view-text">
                  {zipCode === "" ? alertMessage : zipCode}
                </div>
              </div>
            </div>
            {!isQuickAlert && !isEditQuickAlert && (
              <div className="alert-view-label">
                Your alert will be sent now.
              </div>
            )}
          </Col>
        </Row>
      </div>
      <div>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="btn-style"
            onClick={handleNextButtonClick}
            disabled={isAnyFieldEmpty}
          >
            Next
          </Button>
        </Modal.Footer>
      </div>
    </div>
  );
};

export default AdjustTimeAndText;
